import Container from "@/components/Container/Container";
import Button from "@/components/Button/Button";
import Section from "@/components/Section/Section";
import Card from "@/components/Card/Card";
import CTASection from "@/components/CTASection/CTASection";
import HeroAccent from "@/components/HeroAccent/HeroAccent";
import SproutAnimation from "@/components/SproutAnimation/SproutAnimation";
import { site } from "@/content/site";
import styles from "./HomePage.module.css";

const focusAreas = [
  {
    title: "Trauma & nervous system",
    body: "Work with the body's stored experiences to restore a sense of safety and regulation.",
  },
  {
    title: "Anxiety & chronic stress",
    body: "Understand the roots of anxiety and develop a different relationship to stress.",
  },
  {
    title: "Relationships & couples",
    body: "Slow down recurring patterns and rebuild meaningful connection together.",
  },
  {
    title: "Life transitions",
    body: "Find support through career shifts, parenthood, relocations, and identity changes.",
  },
];

const sessionElements = [
  "Reflective conversation that helps clarify patterns and experiences",
  "Somatic awareness to support nervous system regulation",
  "Brain-based approaches for processing trauma or emotional blocks",
  "Practical strategies that support real change in daily life",
];

export default function HomePage() {
  return (
    <>
      <section className={styles.hero}>
        <Container width="default" className={styles.heroInner}>
          <div className={styles.heroCopy}>
            <h1 className={styles.heroTitle}>
              Insight. Regulation.
              <br />
              Meaningful Change.
            </h1>
            <p className={styles.heroLead}>
              Therapy provides an opportunity to better understand the deeper patterns shaping
              your emotional experiences and relationships. Through an integrative approach
              combining Brainspotting, somatic therapy, systemic perspectives, and
              solution-focused work, we move beyond surface-level insight toward lasting
              transformation.
            </p>
            <div className={styles.heroActions}>
              <Button as="link" to="/contact" variant="primary" size="lg">
                Schedule a consultation
              </Button>
              <Button as="a" href={site.links.headway} variant="secondary" size="lg">
                Verify insurance
              </Button>
            </div>
          </div>
          <div className={styles.heroVisual}>
            <div className={styles.portraitFrame}>
              <div className={styles.portraitGlow} aria-hidden="true" />
              <img
                src="/images/lea-portrait-1000.webp"
                srcSet="/images/lea-portrait-750.webp 750w, /images/lea-portrait-1000.webp 1000w, /images/lea-portrait-1500.webp 1500w"
                sizes="(max-width: 720px) 80vw, (max-width: 960px) 60vw, 460px"
                alt="Portrait of Lea Walchshauser, Licensed Marriage and Family Therapist"
                width={1000}
                height={1097}
                className={styles.portrait}
                loading="eager"
                fetchPriority="high"
              />
              <span className={styles.portraitCaption}>Lea Walchshauser, LMFT</span>
            </div>
          </div>
        </Container>
      </section>

      <Section tone="default" spacing="loose">
        <Container width="default">
          <div className={styles.intro}>
            <div className={styles.introCopy}>
              <span className="eyebrow">A grounded space</span>
              <h2 className={styles.introTitle}>
                A thoughtful therapeutic space to explore, process, and grow.
              </h2>
              <p className={styles.introBody}>
                My goal is to help you understand the patterns behind your experiences,
                reconnect with your own internal wisdom, and create the kind of life and
                relationships you want.
              </p>
              <Button as="link" to="/about" variant="ghost" size="md">
                More about my approach &nbsp;→
              </Button>
            </div>
            <div className={styles.introVisual}>
              <SproutAnimation />
            </div>
          </div>
        </Container>
      </Section>

      <Section tone="soft" spacing="loose">
        <Container width="default">
          <div className={styles.sectionHeader}>
            <span className="eyebrow">Areas of focus</span>
            <h2>Common reasons clients reach out.</h2>
          </div>
          <div className={styles.focusGrid}>
            {focusAreas.map((area) => (
              <Card key={area.title} title={area.title} body={area.body} />
            ))}
          </div>
          <div className={styles.sectionFooter}>
            <Button as="link" to="/areas-of-focus" variant="secondary" size="md">
              Explore all areas of focus
            </Button>
          </div>
        </Container>
      </Section>

      <Section spacing="loose">
        <Container width="default" className={styles.split}>
          <div className={styles.splitCopy}>
            <span className="eyebrow">What therapy with me is like</span>
            <h2>Collaborative, thoughtful, and paced to your needs.</h2>
            <p>
              Beginning therapy can feel both hopeful and uncertain. My goal is to create a
              space where you feel safe, understood, and supported while engaging in meaningful
              work toward change.
            </p>
            <p>
              I believe people already hold deep insight and wisdom about their lives. Part of
              therapy is helping bring that awareness forward in a way that feels empowering
              rather than overwhelming.
            </p>
          </div>
          <ul className={styles.elementsList}>
            {sessionElements.map((item, idx) => (
              <li key={item}>
                <span className={styles.elementIndex}>0{idx + 1}</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </Container>
      </Section>

      <Section tone="default" spacing="default">
        <Container width="default">
          <div className={styles.telehealth}>
            <div className={styles.telehealthCopy}>
              <span className="eyebrow">Telehealth across California</span>
              <h3 className={styles.telehealthTitle}>Therapy from the comfort of home.</h3>
              <p>
                I provide secure telehealth therapy for clients located anywhere in California.
                Online therapy can be particularly helpful for trauma work because it allows
                clients to remain in a familiar, safe environment while engaging in treatment.
              </p>
            </div>
            <div className={styles.telehealthAccent} aria-hidden="true">
              <HeroAccent variant="waves" />
            </div>
          </div>
        </Container>
      </Section>

      <CTASection />
    </>
  );
}
